import { Injectable } from '@angular/core';
import {
    from,
    Observable,
    of
} from 'rxjs';
import {
    Client,
    Config,
    createInstance
} from '@optimizely/optimizely-sdk';
import {
    map,
    switchMap
} from 'rxjs/operators';
import { DatiqAppConfig } from '@dat/datiq-core/models';

@Injectable({
    providedIn: 'root',
})
export class DatiqOptimizelyInitializerService {
    constructor() {}

    initialize(config: DatiqAppConfig): Observable<Client> {
        return this.createClient$(config).pipe(
            switchMap((optimizely: Client) =>
                !!optimizely ? this.loadClient(optimizely) : of(null)
            )
        );
    }

    loadClient(optimizely: Client): Observable<Client> {
        return from(
            new Promise<Client>((res, rej) => {
                optimizely
                    .onReady()
                    .then(
                        ({
                            success,
                            reason,
                        }: {
                            success: boolean;
                            reason: string;
                        }) => {
                            !!success
                                ? res(optimizely)
                                : rej({ message: reason });
                        }
                    )
                    .catch((err: any) => rej(err));
            })
        );
    }

    createClient$(appConfig: DatiqAppConfig): Observable<Client> {
        return this.shouldLoad(appConfig)
            ? this.getConfig$(appConfig).pipe(
                  map(
                      (clientConfig: Config): Client =>
                          createInstance(clientConfig)
                  )
              )
            : of(null);
    }

    getConfig$(appConfig: DatiqAppConfig): Observable<Config> {
        const dataFile$: Observable<object> = this.fetchDataFile$(appConfig);
        const logLevel: number = this.getConfigLogLevel(appConfig);
        const sdkKey: string = this.getSdkKey(appConfig);

        return dataFile$.pipe(
            map(
                (datafile: object): Config => ({
                    datafile,
                    sdkKey,
                    logLevel,
                })
            )
        );
    }
    fetchDataFile$(appConfig: DatiqAppConfig): Observable<object> {
        return from(
            fetch(this.getFileUrl(appConfig), {
                mode: 'cors',
                cache: 'no-store',
            }).then((res: Response) => res.json())
        );
    }
    getSdkKey(appConfig: DatiqAppConfig): string {
        return appConfig?.optimizelyConfig?.sdkKey;
    }

    getConfigLogLevel(appConfig: DatiqAppConfig): number {
        return appConfig?.optimizelyConfig?.logLevel;
    }

    shouldLoad(appConfig: DatiqAppConfig) {
        const fileUrl: string = this.getFileUrl(appConfig);

        return fileUrl?.length;
    }

    getUpdateInterval(appConfig: DatiqAppConfig): number {
        return appConfig?.optimizelyConfig?.fetchPollingSeconds * 1000;
    }

    getFileUrl(appConfig: DatiqAppConfig): string {
        return appConfig?.optimizelyConfig?.dataFileUrl;
    }
}
